import { Navigate } from "src/router";
import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { lazyNamed } from "src/utils/lazy-named";

import { useEventSyncOutletContext, isForwardingOutletContext } from "./outlet";

const CreateEventWarehouseSync = lazyNamed(
  () => import("./create"),
  "CreateEventWarehouseSync",
);
const EventSync = lazyNamed(() => import("./common"), "EventSync");
const eventSyncsImport = () => import("./syncs");
const EventWarehouseSyncs = lazyNamed(eventSyncsImport, "EventWarehouseSyncs");

export const preloads: RoutePreload[] = [["/events/syncs", eventSyncsImport]];
const Schedule = lazyNamed(() => import("./common/schedule"), "Schedule");
const Archive = lazyNamed(() => import("./common/archive"), "Archive");
const Runs = lazyNamed(() => import("./common/runs"), "Runs");
const Configuration = lazyNamed(
  () => import("./common/configuration"),
  "Configuration",
);
const ForwardingSyncFailuresArchived = lazyNamed(
  () => import("./forwarding-sync/errors"),
  "ForwardingSyncFailuresArchived",
);
const ForwardingSyncFailuresRetrying = lazyNamed(
  () => import("./forwarding-sync/errors"),
  "ForwardingSyncFailuresRetrying",
);
const Overview = lazyNamed(() => import("./common/overview"), "Overview");
const WarehouseSyncRun = lazyNamed(
  () => import("./warehouse-sync/run"),
  "WarehouseSyncRun",
);
const EventSyncAlerting = lazyNamed(
  () => import("./common/alerting"),
  "EventSyncAlerting",
);
const EventSyncSources = lazyNamed(
  () => import("./common/sources"),
  "EventSyncSources",
);

const SyncIndex = () => {
  const context = useEventSyncOutletContext();

  if (isForwardingOutletContext(context)) {
    return (
      <Navigate to={`/events/syncs/${context.sync.id}/overview`} replace />
    );
  }

  return <Navigate to={`/events/syncs/${context.sync.id}/runs`} replace />;
};

export const routes: Array<RouteObject> = [
  {
    index: true,
    spacesAccess: "hidden-from-limited-view",
    element: <EventWarehouseSyncs />,
  },
  {
    path: "new",
    spacesAccess: "hidden-from-limited-view",
    element: <CreateEventWarehouseSync />,
  },
  {
    path: ":id",
    spacesAccess: "hidden-from-limited-view",
    element: <EventSync />,
    children: [
      {
        index: true,
        spacesAccess: "hidden-from-limited-view",
        element: <SyncIndex />,
      },
      {
        path: "alerting",
        spacesAccess: "hidden-from-limited-view",
        element: <EventSyncAlerting />,
      },
      {
        path: "sources",
        spacesAccess: "hidden-from-limited-view",
        element: <EventSyncSources />,
      },
      {
        path: "overview",
        spacesAccess: "hidden-from-limited-view",
        element: <Overview />,
      },
      {
        path: "configuration",
        spacesAccess: "hidden-from-limited-view",
        element: <Configuration />,
      },
      {
        path: "configuration/event/:event?",
        spacesAccess: "hidden-from-limited-view",
        element: <Configuration />,
      },
      {
        path: "errors",
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="retrying" replace />,
          },
          {
            path: "archived",
            spacesAccess: "hidden-from-limited-view",
            element: <ForwardingSyncFailuresArchived />,
          },
          {
            path: "retrying",
            spacesAccess: "hidden-from-limited-view",
            element: <ForwardingSyncFailuresRetrying />,
          },
        ],
      },
      {
        path: "runs",
        spacesAccess: "hidden-from-limited-view",
        element: <Runs />,
      },
      {
        path: "archive",
        spacesAccess: "hidden-from-limited-view",
        element: <Archive />,
      },
      {
        path: "schedule",
        spacesAccess: "hidden-from-limited-view",
        element: <Schedule />,
      },
    ],
  },
  {
    path: ":id/runs/:runId",
    spacesAccess: "hidden-from-limited-view",
    element: <WarehouseSyncRun />,
  },
];
